import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { SystemTile } from './SystemTile';
import useDeviceIntegrations, {
  mapIntegrationTechnology,
} from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';
import { ServiceState } from '@ekkogmbh/apisdk';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';
import { EditOutlined } from '@mui/icons-material';
import { StatusDot } from './StatusDot';

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant={'caption'}
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

// ---------------------------------------------------------------------------
// Breakdown bar (battery / signal aggregate statistics)
// ---------------------------------------------------------------------------

interface BreakdownSegment {
  label: string;
  value: number;
  color: string;
}

export const BreakdownBar = ({
  title,
  segments,
}: {
  title: string;
  segments: BreakdownSegment[];
}): React.JSX.Element | null => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (total === 0) {
    return null;
  }
  return (
    <Stack spacing={0.75}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>
          {title}
        </Typography>
        <Typography variant={'caption'} color={'text.secondary'}>
          {total.toLocaleString()}
        </Typography>
      </Stack>
      <Stack direction={'row'} sx={{ height: 6, borderRadius: 3, overflow: 'hidden' }}>
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <Tooltip key={segment.label} arrow title={`${segment.label}: ${segment.value.toLocaleString()}`}>
              <Box sx={{ flexGrow: segment.value, bgcolor: segment.color, minWidth: 2 }} />
            </Tooltip>
          ))}
      </Stack>
      <Stack direction={'row'} spacing={1.5} flexWrap={'wrap'}>
        {segments.map((segment) => (
          <Stack key={segment.label} direction={'row'} spacing={0.5} alignItems={'center'}>
            <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: segment.color }} />
            <Typography variant={'caption'} color={'text.secondary'}>
              {segment.label} {segment.value.toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Configuration key/value list (passwords masked)
// ---------------------------------------------------------------------------

const SECRET_KEY_PATTERN = /password|secret|token|auth/i;

const ConfigurationList = ({ configuration }: { configuration: Record<string, unknown> }): React.JSX.Element => (
  <Stack spacing={0.75}>
    {Object.entries(configuration).map(([key, value]) => (
      <Stack key={key} direction={'row'} spacing={2} alignItems={'baseline'}>
        <Typography variant={'caption'} color={'text.secondary'} sx={{ minWidth: 150, flexShrink: 0 }}>
          {key}
        </Typography>
        <Typography variant={'body2'} sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
          {SECRET_KEY_PATTERN.test(key) ? '••••••••' : String(value)}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

export const IntegrationDetailsDrawer = ({ onEdit }: { onEdit: () => void }): React.JSX.Element => {
  const { selectedIntegration: integration } = useDeviceIntegrations();

  const content = useMemo(() => {
    if (integration === undefined) {
      return <></>;
    }

    const { serviceData, coordinate, metadata } = integration;
    const serviceTechnology = mapIntegrationTechnology(integration.integrationName);
    const isEditable = metadata?.env !== true;

    const headerElement = (
      <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ minWidth: 0 }}>
        {serviceTechnology.logo ? (
          <Box
            component={'img'}
            src={serviceTechnology.logo}
            alt={serviceTechnology.name}
            sx={{ height: 36, maxWidth: 200, objectFit: 'contain' }}
          />
        ) : (
          <SystemTile system={serviceTechnology} size={40} />
        )}
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          {!serviceTechnology.logo && (
            <Typography variant={'h6'} fontWeight={700} noWrap>
              {serviceTechnology.name}
            </Typography>
          )}
          {serviceTechnology.manufacturer && (
            <Typography variant={'caption'} color={'text.secondary'} noWrap>
              {`by ${serviceTechnology.manufacturer.name}`}
            </Typography>
          )}
        </Stack>
      </Stack>
    );

    return (
      <SectionDrawerContent
        header={headerElement}
        actions={
          isEditable
            ? {
                confirm: {
                  onClick: onEdit,
                  label: 'Edit',
                  icon: <EditOutlined />,
                },
              }
            : undefined
        }
      >
        <Stack spacing={4}>
          {/* Connection */}
          <Stack spacing={1.5}>
            <SectionHeader title={'Connection'} />
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Stack spacing={0.5}>
                <StatusDot state={serviceData?.state as ServiceState} />
                <Typography variant={'body2'} sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                  {coordinate}
                </Typography>
                {/* serviceData?.reachability && (
                      <Typography variant={'caption'} color={serviceData.reachability ? 'text.secondary' : 'error'}>
                        {status.reachability.message}
                        {status.lastCheckedAt && ` · checked ${moment(status.lastCheckedAt).fromNow()}`}
                      </Typography>
                    )*/}
              </Stack>
              {/*<Button size={'small'} variant={'outlined'} startIcon={<SyncOutlined />} onClick={runCheck}>
                    Check
                  </Button>*/}
            </Stack>
          </Stack>

          {/* Devices (aggregate only — device management lives elsewhere) */}
          {/*<Stack spacing={1.5}>
                <SectionHeader title={'Devices'} />
                <DeviceCounts status={status} systemName={resolved.system.name} />
              </Stack>*/}

          {/* Statistics as reported by the external system. These describe
                  the system's OWN entities — not this platform's data. */}
          {/*(status?.battery || status?.signal || status?.accessPoints) && (
                <Stack spacing={1.5}>
                  <SectionHeader title={`${resolved.system.name} statistics`} />
                  <Paper
                    variant={'outlined'}
                    sx={{ p: 2, borderRadius: 2, borderStyle: 'dashed', backgroundColor: 'action.hover' }}
                  >
                    <Stack spacing={2}>
                      <Stack direction={'row'} spacing={0.75} alignItems={'center'}>
                        <CloudSyncOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant={'caption'} color={'text.secondary'}>
                          Reported by {resolved.system.name} about its own entities — not data of this platform.
                        </Typography>
                      </Stack>
                      {status.accessPoints && (
                        <BreakdownBar
                          title={'Access points'}
                          segments={[
                            { label: 'connected', value: status.accessPoints.connected, color: 'success.main' },
                            { label: 'not connected', value: status.accessPoints.notConnected, color: 'error.main' },
                          ]}
                        />
                      )}
                      {status.battery && (
                        <BreakdownBar
                          title={'Battery'}
                          segments={[
                            { label: 'good', value: status.battery.good, color: 'success.main' },
                            { label: 'bad', value: status.battery.bad, color: 'error.main' },
                          ]}
                        />
                      )}
                      {status.signal && (
                        <BreakdownBar
                          title={'Signal'}
                          segments={[
                            { label: 'perfect', value: status.signal.perfect, color: 'success.main' },
                            { label: 'good', value: status.signal.good, color: 'success.light' },
                            { label: 'average', value: status.signal.average, color: 'warning.main' },
                            { label: 'poor', value: status.signal.poor, color: 'error.main' },
                          ]}
                        />
                      )}
                    </Stack>
                  </Paper>
                </Stack>
              )*/}

          {/* Device types (per-type capability breakdown) */}
          {/*<Stack spacing={1.5}>
                <SectionHeader title={'Device types'} />
                <Stack spacing={1.25}>
                  {resolved.connector.deviceTypes.map((deviceType) => (
                    <Stack
                      key={deviceType.key}
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      spacing={2}
                    >
                      <Typography variant={'body2'} sx={{ minWidth: 110, flexShrink: 0 }}>
                        {deviceType.name}
                      </Typography>
                      <CapabilityChips capabilities={deviceType.capabilities} />
                    </Stack>
                  ))}
                </Stack>
              </Stack>*/}

          {/* Configuration */}
          <Stack spacing={1.5}>
            <SectionHeader title={'Configuration'} />
            <ConfigurationList configuration={integration.configuration ?? {}} />
          </Stack>
        </Stack>
      </SectionDrawerContent>
    );
  }, [integration]);

  return content;
};
